import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import {
  CalendarClock,
  CreditCard,
  Download,
  FileSpreadsheet,
  FileText,
  FileWarning,
  HardDrive,
  Loader2,
  RefreshCw,
  Route,
  ShieldAlert,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { api, API_BASE_URL } from "../../../lib/axios";
import {
  CommercialInvoicePreview,
  type CommercialInvoicePreviewDto,
} from "../../../pages/placeholders/CommercialInvoicePreview";
import { useAuth } from "../../../context/AuthContext";
import { canAccessFinanceModule } from "../../../utils/roleAccess";
import FinanceAccessDenied from "../../../components/finance/FinanceAccessDenied";
import FinanceApiErrorBanner from "../../../components/finance/FinanceApiErrorBanner";
import EncaissementFactureModal, {
  type FactureEncaissementContext,
} from "../../../components/finance/EncaissementFactureModal";
import {
  AdminDataTableShell,
  AdminEmptyState,
  AdminKpiCard,
  AdminPageHero,
  adminHeroActionClassName,
} from "../../../modules/admin/ui";

/** Aligné backend `InvoiceFinancialStateDto` (liste admin). */
export type InvoiceFinancialStateListDto = {
  invoiceId: number;
  invoiceNumber: string;
  totalTtc: number;
  totalPaid: number;
  remainingBalance: number;
  invoiceStatus: string;
  paymentStatus: string;
  latestReceiptType: "NONE" | "DEPOSIT_RECEIPT" | "PAYMENT_QUITTANCE";
  latestReceiptPaymentId: number | null;
  latestReceiptNumber: string | null;
  canPay: boolean;
  canDownloadInvoice: boolean;
  canDownloadReceipt: boolean;
  hasInvoicePdf: boolean;
  hasReceiptPdf: boolean;
};

type FactureRow = {
  id: number;
  numero: string;
  montantTTC: number;
  montantPaye: number;
  devise: string;
  dateEmission: string;
  dateEcheance?: string | null;
  statut: string;
  fichierPDF?: string | null;
  pdfPath?: string | null;
  pdfFileSize?: number | null;
  client?: {
    id: number;
    nom?: string | null;
    prenom?: string | null;
    nomEntreprise?: string | null;
  } | null;
  /** Vérité métier officielle (`GET /api/factures`). */
  financialState: InvoiceFinancialStateListDto;
};

function clientLabel(f: FactureRow): string {
  const c = f.client;
  if (!c) return "—";
  return (
    (c.nomEntreprise?.trim() ||
      [c.prenom, c.nom].filter(Boolean).join(" ").trim() ||
      `Client #${c.id}`) as string
  );
}

type BillingIntegritySummary = {
  totalIssues: number;
  severity: "ok" | "amber" | "red";
  counts: Record<string, number>;
};

type DocIntegrityReport = {
  orphanFiles: string[];
  missingFiles: Array<{ factureId: number; key: string; numero: string }>;
  corruptedFiles: Array<{ factureId: number; key: string; reason: string }>;
  invalidChecksums: Array<{ factureId: number; key: string }>;
  stalePdfLocks: number;
  stalePdfLocksCleared: number;
  storageSize: number;
  facturePdfCount: number;
  summary: { totalIssues: number; severity: "ok" | "amber" | "red" };
};

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0 o";
  const u = ["o", "Ko", "Mo", "Go"];
  let i = 0;
  let x = n;
  while (x >= 1024 && i < u.length - 1) {
    x /= 1024;
    i++;
  }
  return `${x >= 10 || i === 0 ? Math.round(x) : x.toFixed(1)} ${u[i]}`;
}

function statutBadge(statut: string): string {
  const s = statut.toUpperCase();
  if (s === "PAYEE") return "bg-emerald-500/20 text-emerald-300 ring-emerald-500/30";
  if (s === "PARTIELLEMENT_PAYEE") return "bg-amber-500/20 text-amber-200 ring-amber-500/30";
  if (s === "IMPAYEE") return "bg-rose-500/20 text-rose-200 ring-rose-500/30";
  if (s === "BROUILLON") return "bg-slate-700/60 text-slate-200 ring-slate-500/35";
  if (s === "ANNULEE" || s === "REMBOURSEE") return "bg-slate-600/35 text-slate-300 ring-slate-600/35";
  return "bg-slate-600/40 text-slate-200 ring-slate-500/30";
}

function receiptDownloadLabel(fs: InvoiceFinancialStateListDto): string {
  if (fs.latestReceiptType === "DEPOSIT_RECEIPT") return "Reçu acompte";
  if (fs.latestReceiptType === "PAYMENT_QUITTANCE") return "Quittance";
  return "Reçu / quittance";
}

export default function FinanceFacturesPage(): React.JSX.Element {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const baseDetail = "/admin/finance/factures";

  const [factures, setFactures] = useState<FactureRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const [filtreClient, setFiltreClient] = useState("");
  const [filtreEtat, setFiltreEtat] = useState("");

  const [modalMensuelle, setModalMensuelle] = useState(false);
  const [modalPassages, setModalPassages] = useState(false);
  const [monthlyGenerating, setMonthlyGenerating] = useState(false);

  const isAdmin = String(user?.role ?? "").toUpperCase() === "ADMIN";
  const [integritySummary, setIntegritySummary] = useState<BillingIntegritySummary | null>(null);
  const [integrityLoading, setIntegrityLoading] = useState(false);
  const [integrityError, setIntegrityError] = useState<string | null>(null);

  const [docIntegrity, setDocIntegrity] = useState<DocIntegrityReport | null>(null);
  const [docIntegrityLoading, setDocIntegrityLoading] = useState(false);
  const [docIntegrityError, setDocIntegrityError] = useState<string | null>(null);
  const [encaissementFacture, setEncaissementFacture] =
    useState<FactureEncaissementContext | null>(null);
  const [pdfBusyKey, setPdfBusyKey] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState<"excel" | "pdf" | null>(null);
  const [invoicePreview, setInvoicePreview] = useState<CommercialInvoicePreviewDto | null>(null);

  function absoluteUploadUrl(path: string | null | undefined): string | null {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;
    const root = API_BASE_URL.replace(/\/api\/?$/i, "");
    return root ? `${root}${path.startsWith("/") ? path : `/${path}`}` : path;
  }

  /** Même fichier que pour le téléchargement facture (`GET /api/factures/:id/pdf`). */
  const downloadFacturePdfBlob = async (factureId: number, numero?: string | null) => {
    try {
      const res = await api.get(`/factures/${factureId}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = String(numero ?? factureId).replace(/[^\w.-]+/g, "_") || String(factureId);
      a.download = `facture-${safe}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement du PDF impossible.");
    }
  };

  const openFacturePdfInNewTab = async (factureId: number) => {
    try {
      const res = await api.get(`/factures/${factureId}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Impossible d’ouvrir le PDF facture.");
    }
  };

  /** Comportement portail client : même aperçu commercial, puis fallback PDF blob. */
  const openFacturePdf = async (factureId: number) => {
    const key = `inv-${factureId}`;
    try {
      setPdfBusyKey(key);
      const { data } = await api.get<{ success?: boolean; data?: CommercialInvoicePreviewDto }>(
        `/factures/${factureId}/commercial-preview`,
      );
      if (data?.success && data.data) {
        setInvoicePreview(data.data);
        return;
      }
      throw new Error("NO_PREVIEW");
    } catch {
      try {
        await openFacturePdfInNewTab(factureId);
      } catch {
        toast.error("Impossible d’ouvrir le PDF facture.");
      }
    } finally {
      setPdfBusyKey(null);
    }
  };

  const openReceiptPdf = async (paiementId: number) => {
    const key = `rec-${paiementId}`;
    try {
      setPdfBusyKey(key);
      /** Aligné portail (`streamPaymentReceiptPdfForRequest`). */
      const res = await api.get(`/finance/paiements/${paiementId}/receipt/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      toast.error("Impossible d’ouvrir le reçu / quittance.");
    } finally {
      setPdfBusyKey(null);
    }
  };

  const openEncaissement = (facture: FactureRow) => {
    const fs = facture.financialState;
    if (!fs.canPay || fs.remainingBalance <= 0) {
      toast.info("Encaissement indisponible pour cette facture (soldée ou verrouillage métier).");
      return;
    }
    if (!facture.client?.id) {
      toast.error("Client introuvable pour cette facture.");
      return;
    }

    setEncaissementFacture({
      id: facture.id,
      numero: facture.numero,
      clientLabel: clientLabel(facture),
      montantTTC: fs.totalTtc,
      montantPaye: fs.totalPaid,
      resteAPayer: fs.remainingBalance,
      devise: facture.devise || "USD",
      clientId: facture.client.id,
    });
  };

  const loadIntegrity = useCallback(async () => {
    if (!isAdmin) return;
    setIntegrityLoading(true);
    setIntegrityError(null);
    try {
      const { data } = await api.get<{ success?: boolean; report?: { summary: BillingIntegritySummary } }>(
        "/admin/billing/integrity"
      );
      setIntegritySummary(data?.report?.summary ?? null);
    } catch {
      setIntegrityError("Impossible de charger le rapport d’intégrité facturation.");
      setIntegritySummary(null);
    } finally {
      setIntegrityLoading(false);
    }
  }, [isAdmin]);

  const loadDocIntegrity = useCallback(async () => {
    if (!isAdmin) return;
    setDocIntegrityLoading(true);
    setDocIntegrityError(null);
    try {
      const { data } = await api.get<
        DocIntegrityReport & { success?: boolean }
      >("/admin/storage/integrity");
      if (data && typeof data.summary === "object") {
        const {
          orphanFiles,
          missingFiles,
          corruptedFiles,
          invalidChecksums,
          stalePdfLocks,
          stalePdfLocksCleared,
          storageSize,
          facturePdfCount,
          summary,
        } = data;
        setDocIntegrity({
          orphanFiles: orphanFiles ?? [],
          missingFiles: missingFiles ?? [],
          corruptedFiles: corruptedFiles ?? [],
          invalidChecksums: invalidChecksums ?? [],
          stalePdfLocks: stalePdfLocks ?? 0,
          stalePdfLocksCleared: stalePdfLocksCleared ?? 0,
          storageSize: storageSize ?? 0,
          facturePdfCount: facturePdfCount ?? 0,
          summary: summary ?? { totalIssues: 0, severity: "ok" },
        });
      } else {
        setDocIntegrity(null);
      }
    } catch {
      setDocIntegrityError("Impossible de charger le rapport d’intégrité documentaire.");
      setDocIntegrity(null);
    } finally {
      setDocIntegrityLoading(false);
    }
  }, [isAdmin]);

  const load = useCallback(async () => {
    setForbidden(false);
    setServerError(null);
    setLoading(true);
    try {
      const { data } = await api.get<{ success?: boolean; items?: FactureRow[] }>("/factures", {
        params: { page: 1, limit: 100 },
      });
      const rawItems = Array.isArray(data?.items) ? data.items : [];
      if (rawItems.some((row) => !row?.financialState)) {
        toast.error("Réponse factures incomplète : état financier officiel absent. Actualisez la page.");
        setFactures([]);
        return;
      }
      setFactures(rawItems as FactureRow[]);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setForbidden(true);
        setFactures([]);
        return;
      }
      if (isAxiosError(e) && e.response?.status === 500) {
        const msg =
          (e.response?.data as { message?: string })?.message ||
          "Le serveur n’a pas pu charger les factures.";
        setServerError(msg);
        setFactures([]);
        return;
      }
      console.error(e);
      toast.error("Impossible de charger les factures.");
      setFactures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (isAdmin) {
      void loadIntegrity();
      void loadDocIntegrity();
    }
  }, [isAdmin, loadIntegrity, loadDocIntegrity]);

  const filtrees = useMemo(() => {
    return factures.filter((f) => {
      const fs = f.financialState;
      const nom = clientLabel(f).toLowerCase();
      const matchClient = !filtreClient || nom.includes(filtreClient.toLowerCase());
      const matchEtat = !filtreEtat || fs.invoiceStatus === filtreEtat;
      return matchClient && matchEtat;
    });
  }, [factures, filtreClient, filtreEtat]);

  const kpis = useMemo(() => {
    const total = filtrees.length;
    const payees = filtrees.filter((f) => f.financialState.invoiceStatus === "PAYEE").length;
    const impayees = filtrees.filter((f) => f.financialState.invoiceStatus === "IMPAYEE").length;
    const encours = filtrees.reduce((s, f) => s + Math.max(0, f.financialState.remainingBalance), 0);
    return { total, payees, impayees, encours };
  }, [filtrees]);

  const handleGenerateMonthlySubscriptionInvoices = useCallback(async () => {
    setMonthlyGenerating(true);
    try {
      const { data } = await api.post<{ message?: string; created?: number; existing?: number }>(
        "/factures/abonnements/mensuelles"
      );
      const created = Number(data?.created ?? 0);
      const existing = Number(data?.existing ?? 0);
      toast.success(
        data?.message ??
          (created > 0
            ? `${created} facture(s) mensuelle(s) créée(s).`
            : `Aucune facture manquante (${existing} déjà existante(s)).`)
      );
      setModalMensuelle(false);
      await load();
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 400) {
        toast.error("Période de facturation invalide.");
      } else {
        toast.error("Impossible de générer les factures mensuelles d’abonnement.");
      }
    } finally {
      setMonthlyGenerating(false);
    }
  }, [load]);

  const exportFactures = async (format: "excel" | "pdf") => {
    setExportBusy(format);
    try {
      const res = await api.get("/factures/export", {
        params: {
          format,
          search: filtreClient.trim() || undefined,
          statut: filtreEtat || undefined,
        },
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type:
          format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `factures-${Date.now()}.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(format === "excel" ? "Export Excel téléchargé." : "Export PDF téléchargé.");
    } catch {
      toast.error("Export impossible pour le moment.");
    } finally {
      setExportBusy(null);
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
      </div>
    );
  }

  if (!user || !canAccessFinanceModule(user.role)) {
    return <FinanceAccessDenied />;
  }

  if (forbidden) {
    return <FinanceAccessDenied title="Accès refusé" detail="L’API a refusé la lecture des factures (403)." />;
  }

  return (
    <div className="flex w-full min-w-0 max-w-none flex-col gap-6">
      {invoicePreview ? (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-black/80 p-4">
          <div className="sticky top-0 z-[101] mx-auto mb-4 flex max-w-[980px] flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-700 bg-slate-950/95 px-4 py-3 shadow-xl">
            <div>
              <p className="text-sm font-semibold text-white">Aperçu facture (comme portail client)</p>
              <p className="text-xs text-slate-400">Facture {invoicePreview.facture.numero}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {invoicePreview.pdf.url ? (
                <a
                  href={absoluteUploadUrl(invoicePreview.pdf.url) ?? "#"}
                  download
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500"
                >
                  <Download className="h-4 w-4" aria-hidden />
                  Télécharger PDF (fichier)
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    void downloadFacturePdfBlob(invoicePreview.facture.id, invoicePreview.facture.numero)
                  }
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500"
                >
                  <Download className="h-4 w-4" aria-hidden />
                  Télécharger PDF
                </button>
              )}
              <button
                type="button"
                onClick={() => void openFacturePdfInNewTab(invoicePreview.facture.id)}
                className="inline-flex items-center gap-2 rounded-lg border border-sky-600/70 px-3 py-2 text-sm font-medium text-sky-100 hover:bg-slate-800"
              >
                Ouvrir PDF (onglet)
              </button>
              <button
                type="button"
                onClick={() => setInvoicePreview(null)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
              >
                <X className="h-4 w-4" aria-hidden />
                Fermer
              </button>
            </div>
          </div>
          <div className="mx-auto max-w-[980px]">
            <CommercialInvoicePreview data={invoicePreview} />
          </div>
        </div>
      ) : null}

      <AdminPageHero
        eyebrow="Finance"
        title="Facturation"
        subtitle="Contrat / abonnement → passages validés → facture → paiement → recouvrement si impayé."
        actions={
          <>
            <button
              type="button"
              onClick={() => void load()}
              className={adminHeroActionClassName(false)}
            >
              <RefreshCw className={`mr-2 inline h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <button
              type="button"
              onClick={() => void exportFactures("excel")}
              disabled={exportBusy !== null}
              className={adminHeroActionClassName(false)}
            >
              {exportBusy === "excel" ? (
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <FileSpreadsheet className="mr-2 inline h-4 w-4" aria-hidden />
              )}
              Export Excel
            </button>
            <button
              type="button"
              onClick={() => void exportFactures("pdf")}
              disabled={exportBusy !== null}
              className={adminHeroActionClassName(false)}
            >
              {exportBusy === "pdf" ? (
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <FileText className="mr-2 inline h-4 w-4" aria-hidden />
              )}
              Export PDF
            </button>
            <Link to="/admin/recouvrement" className={adminHeroActionClassName(false)}>
              Recouvrement
            </Link>
          </>
        }
      />

      {serverError ? (
        <FinanceApiErrorBanner message={serverError} onRetry={() => void load()} />
      ) : null}

      <section className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4 text-sm text-slate-400">
        <p className="font-medium text-slate-200">Règles métier</p>
        <ul className="mt-2 list-inside list-disc space-y-1">
          <li>Facturation automatique depuis les passages : uniquement si les passages sont validés.</li>
          <li>
            Facture exceptionnelle : doit être explicitement justifiée et signalée comme telle à la création.
          </li>
          <li>
            Proforma : réservée aux prestations ponctuelles, chantiers, urgences ou devis client — pas au cycle
            d’abonnement récurrent.
          </li>
        </ul>
      </section>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <AdminKpiCard label="Factures (filtre)" value={String(kpis.total)} hint="Liste courante" />
        <AdminKpiCard label="Payées" value={String(kpis.payees)} />
        <AdminKpiCard label="Impayées" value={String(kpis.impayees)} />
        <AdminKpiCard
          label="Reste à encaisser (filtre)"
          value={`${kpis.encours.toLocaleString("fr-FR")}`}
          hint="Somme officielle des soldes résiduels (`remainingBalance`)"
        />
      </div>

      {isAdmin ? (
        <div
          className={`rounded-2xl border p-4 ${
            integritySummary?.severity === "red"
              ? "border-rose-500/40 bg-rose-950/25"
              : integritySummary?.severity === "amber"
                ? "border-amber-500/40 bg-amber-950/25"
                : "border-emerald-500/35 bg-emerald-950/20"
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <ShieldAlert
                className={`mt-0.5 h-5 w-5 shrink-0 ${
                  integritySummary?.severity === "red"
                    ? "text-rose-300"
                    : integritySummary?.severity === "amber"
                      ? "text-amber-200"
                      : "text-emerald-300"
                }`}
                aria-hidden
              />
              <div>
                <p className="text-sm font-semibold text-white">Intégrité financière</p>
                <p className="mt-1 text-xs text-slate-400">
                  Contrôles passage ↔ facture (orphelins, doublons, totaux). Voir{" "}
                  <span className="font-mono text-slate-300">GET /api/admin/billing/integrity</span>.
                </p>
                {integrityError ? (
                  <p className="mt-2 text-xs text-rose-300">{integrityError}</p>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={() => void loadIntegrity()}
              disabled={integrityLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-900/80 px-3 py-1.5 text-xs text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {integrityLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              )}
              Actualiser
            </button>
          </div>
          {integritySummary && !integrityError ? (
            <div className="mt-4 grid gap-2 text-xs text-slate-300 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-lg bg-slate-950/50 px-3 py-2">
                <span className="text-slate-500">Anomalies totales</span>
                <span className="ml-2 font-mono text-white">{integritySummary.totalIssues}</span>
              </div>
              <div className="rounded-lg bg-slate-950/50 px-3 py-2">
                <span className="text-slate-500">Niveau</span>
                <span className="ml-2 font-medium uppercase text-white">{integritySummary.severity}</span>
              </div>
              {Object.entries(integritySummary.counts)
                .filter(([, n]) => n > 0)
                .slice(0, 6)
                .map(([k, n]) => (
                  <div key={k} className="rounded-lg bg-slate-950/50 px-3 py-2">
                    <span className="text-slate-500">{k}</span>
                    <span className="ml-2 font-mono text-amber-100">{n}</span>
                  </div>
                ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {isAdmin ? (
        <div
          className={`rounded-2xl border p-4 ${
            docIntegrity?.summary.severity === "red"
              ? "border-rose-500/40 bg-rose-950/25"
              : docIntegrity?.summary.severity === "amber"
                ? "border-amber-500/40 bg-amber-950/25"
                : "border-emerald-500/35 bg-emerald-950/20"
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <HardDrive
                className={`mt-0.5 h-5 w-5 shrink-0 ${
                  docIntegrity?.summary.severity === "red"
                    ? "text-rose-300"
                    : docIntegrity?.summary.severity === "amber"
                      ? "text-amber-200"
                      : "text-emerald-300"
                }`}
                aria-hidden
              />
              <div>
                <p className="text-sm font-semibold text-white">Intégrité documentaire</p>
                <p className="mt-1 text-xs text-slate-400">
                  Fichiers PDF factures : orphelins disque, références manquantes, checksums, verrous PDF
                  expirés. Voir{" "}
                  <span className="font-mono text-slate-300">GET /api/admin/storage/integrity</span>.
                </p>
                {docIntegrityError ? (
                  <p className="mt-2 text-xs text-rose-300">{docIntegrityError}</p>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              onClick={() => void loadDocIntegrity()}
              disabled={docIntegrityLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-900/80 px-3 py-1.5 text-xs text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {docIntegrityLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              )}
              Actualiser
            </button>
          </div>
          {docIntegrity && !docIntegrityError ? (
            <div className="mt-4 grid gap-2 text-xs text-slate-300 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-lg bg-slate-950/50 px-3 py-2">
                <span className="text-slate-500">Anomalies</span>
                <span className="ml-2 font-mono text-white">{docIntegrity.summary.totalIssues}</span>
              </div>
              <div className="rounded-lg bg-slate-950/50 px-3 py-2">
                <span className="text-slate-500">Niveau</span>
                <span className="ml-2 font-medium uppercase text-white">{docIntegrity.summary.severity}</span>
              </div>
              <div className="rounded-lg bg-slate-950/50 px-3 py-2">
                <span className="text-slate-500">Orphelins (disque)</span>
                <span className="ml-2 font-mono text-amber-100">{docIntegrity.orphanFiles.length}</span>
              </div>
              <div className="rounded-lg bg-slate-950/50 px-3 py-2">
                <span className="text-slate-500">Manquants (READY)</span>
                <span className="ml-2 font-mono text-rose-200">{docIntegrity.missingFiles.length}</span>
              </div>
              <div className="rounded-lg bg-slate-950/50 px-3 py-2">
                <span className="text-slate-500">Checksum / corrompus</span>
                <span className="ml-2 font-mono text-rose-200">
                  {docIntegrity.corruptedFiles.length}
                </span>
              </div>
              <div className="rounded-lg bg-slate-950/50 px-3 py-2">
                <span className="text-slate-500">Verrous PDF périmés</span>
                <span className="ml-2 font-mono text-amber-100">{docIntegrity.stalePdfLocks}</span>
                {docIntegrity.stalePdfLocksCleared > 0 ? (
                  <span className="ml-1 text-slate-500">
                    (nettoyés : {docIntegrity.stalePdfLocksCleared})
                  </span>
                ) : null}
              </div>
              <div className="rounded-lg bg-slate-950/50 px-3 py-2">
                <span className="text-slate-500">Espace factures</span>
                <span className="ml-2 font-mono text-white">{formatBytes(docIntegrity.storageSize)}</span>
              </div>
              <div className="rounded-lg bg-slate-950/50 px-3 py-2">
                <span className="text-slate-500">PDF READY (nombre)</span>
                <span className="ml-2 font-mono text-white">{docIntegrity.facturePdfCount}</span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <p className="text-sm font-semibold text-white">Créer ou générer une facture</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setModalMensuelle(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700"
          >
            <CalendarClock className="h-4 w-4" />
            Facture mensuelle abonnement
          </button>
          <button
            type="button"
            onClick={() => setModalPassages(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700"
          >
            <Route className="h-4 w-4" />
            Depuis passages validés
          </button>
          <Link
            to="/admin/finance/factures/nouvelle?exceptionnel=1"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500"
          >
            <FileWarning className="h-4 w-4" />
            Facture exceptionnelle
          </Link>
          <Link
            to="/admin/finance/proformas"
            className="inline-flex items-center gap-2 rounded-xl border border-indigo-500/40 bg-indigo-950/40 px-4 py-2 text-sm text-indigo-100 hover:bg-indigo-950/70"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Proformas (ponctuel / devis)
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          placeholder="Filtrer client…"
          value={filtreClient}
          onChange={(e) => setFiltreClient(e.target.value)}
          className="min-w-[200px] flex-1 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500"
        />
        <select
          value={filtreEtat}
          onChange={(e) => setFiltreEtat(e.target.value)}
          className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
        >
          <option value="">Tous les statuts</option>
          <option value="PAYEE">Payée</option>
          <option value="PARTIELLEMENT_PAYEE">Partielle</option>
          <option value="IMPAYEE">Impayée</option>
          <option value="BROUILLON">Brouillon</option>
          <option value="ANNULEE">Annulée</option>
          <option value="REMBOURSEE">Remboursée</option>
        </select>
      </div>

      <AdminDataTableShell>
        {loading ? (
          <div className="flex justify-center py-16 text-slate-400">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : filtrees.length === 0 ? (
          <AdminEmptyState
            title="Aucune facture générée"
            description="Créez une facture depuis un abonnement, des passages validés (après validation terrain), une facture exceptionnelle justifiée, ou une proforma pour un besoin ponctuel."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Link
                  to="/admin/contrats"
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500"
                >
                  Voir les contrats
                </Link>
                <Link
                  to="/admin/passages/historique"
                  className="rounded-xl border border-slate-600 px-4 py-2 text-sm text-white hover:bg-slate-800"
                >
                  Historique passages
                </Link>
              </div>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-slate-200">
              <thead className="border-b border-slate-800 bg-slate-950/80 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Numéro</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Émission</th>
                  <th className="px-4 py-3">Échéance</th>
                  <th className="px-4 py-3 text-right">TTC</th>
                  <th className="px-4 py-3 text-right">Payé</th>
                  <th className="px-4 py-3 text-right">Solde</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3">Document</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtrees.map((f) => {
                  const fs = f.financialState;
                  const payId = fs.latestReceiptPaymentId;
                  return (
                  <tr key={f.id} className="border-b border-slate-800/80 hover:bg-slate-800/40">
                    <td className="px-4 py-3 font-mono text-xs text-white">{f.numero}</td>
                    <td className="px-4 py-3">{clientLabel(f)}</td>
                    <td className="px-4 py-3 text-slate-400">
                      {f.dateEmission ? dayjs(f.dateEmission).format("DD/MM/YYYY") : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {f.dateEcheance ? dayjs(f.dateEcheance).format("DD/MM/YYYY") : "—"}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {fs.totalTtc.toLocaleString("fr-FR")} {f.devise || ""}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-300">
                      {fs.totalPaid.toLocaleString("fr-FR")} {f.devise || ""}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-amber-200">
                      {fs.remainingBalance.toLocaleString("fr-FR")} {f.devise || ""}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${statutBadge(
                          fs.invoiceStatus
                        )}`}
                      >
                        {fs.invoiceStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-xs">
                      {fs.canDownloadInvoice ? (
                        <>
                          <span className="inline-flex rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-xs font-medium text-emerald-200 ring-1 ring-emerald-500/30">
                            Document disponible
                          </span>
                          <button
                            type="button"
                            onClick={() => void openFacturePdf(f.id)}
                            disabled={pdfBusyKey === `inv-${f.id}`}
                            className="mt-1 block text-left text-emerald-400 hover:text-emerald-300 disabled:opacity-50"
                          >
                            {pdfBusyKey === `inv-${f.id}` ? "Ouverture…" : "Voir facture"}
                          </button>
                        </>
                      ) : (
                        <span className="inline-flex rounded-full bg-slate-700/60 px-2.5 py-0.5 text-xs font-medium text-slate-300 ring-1 ring-slate-600/50">
                          Document non disponible
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        {fs.canPay ? (
                          <button
                            type="button"
                            onClick={() => openEncaissement(f)}
                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"
                          >
                            <CreditCard className="h-3.5 w-3.5" />
                            Payer
                          </button>
                        ) : null}
                        {fs.canDownloadReceipt && payId != null ? (
                          <button
                            type="button"
                            onClick={() => void openReceiptPdf(payId)}
                            disabled={pdfBusyKey === `rec-${payId}`}
                            className="inline-flex items-center gap-1 rounded-lg border border-sky-500/50 px-3 py-1.5 text-xs font-semibold text-sky-200 hover:bg-slate-800 disabled:opacity-50"
                          >
                            {pdfBusyKey === `rec-${payId}` ? "Ouverture…" : receiptDownloadLabel(fs)}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => navigate(`${baseDetail}/${f.id}`)}
                          className="rounded-lg border border-slate-600 px-3 py-1.5 text-xs text-slate-100 hover:bg-slate-800"
                        >
                          Détail
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminDataTableShell>

      {modalMensuelle ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4">
          <div className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-slate-700 bg-slate-900 p-5 shadow-2xl sm:rounded-2xl sm:p-6">
            <h3 className="text-lg font-semibold text-white">Facture mensuelle abonnement</h3>
            <p className="mt-2 text-sm text-slate-400">
              Crée côté serveur les factures mensuelles manquantes des abonnements actifs. Un passage marqué
              « inclus abonnement » ne génère pas de facture passage séparée, mais la mensualité du contrat reste due.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void handleGenerateMonthlySubscriptionInvoices()}
                disabled={monthlyGenerating}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500"
              >
                {monthlyGenerating ? "Génération..." : "Générer les factures manquantes"}
              </button>
              <button
                type="button"
                onClick={() => setModalMensuelle(false)}
                disabled={monthlyGenerating}
                className="rounded-xl border border-slate-600 px-4 py-2 text-sm text-white hover:bg-slate-800"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {modalPassages ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4">
          <div className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-slate-700 bg-slate-900 p-5 shadow-2xl sm:rounded-2xl sm:p-6">
            <h3 className="text-lg font-semibold text-white">Facture depuis passages validés</h3>
            <p className="mt-2 text-sm text-slate-400">
              Une facture basée sur les passages ne peut être consolidée que si les passages concernés sont{" "}
              <strong className="text-white">validés</strong> (contrôle terrain / validation siège). Sinon, la
              facturation automatique est bloquée.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/admin/passages/historique"
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500"
              >
                Historique des passages
              </Link>
              <button
                type="button"
                onClick={() => setModalPassages(false)}
                className="rounded-xl border border-slate-600 px-4 py-2 text-sm text-white hover:bg-slate-800"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <EncaissementFactureModal
        open={encaissementFacture != null}
        facture={encaissementFacture}
        onClose={() => setEncaissementFacture(null)}
        onSuccess={() => void load()}
      />
    </div>
  );
}
